import React from "react";
import { useEffect } from "react";

import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { GwpDataRecord, GwpTier } from "@/types/promoData";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

interface GwpBannerModalContentProps {
  data: GwpDataRecord;
  theme?: ThemeTypes;
}

const OrangeBullet: React.FC = () => (
  <svg
    width="4"
    height="4"
    viewBox="0 0 4 4"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="mb-[1px] flex-shrink-0" /* Aligns bullet with first line of text */
  >
    <circle cx="2" cy="2" r="2" fill="#FFC942" />
  </svg>
);

const GwpModalContent: React.FC<GwpBannerModalContentProps> = ({ data, theme = "master" }) => {
  const { items, title } = data;
  const cls = theme === "men" ? "bg-[#14283A]" : "bg-[#065954]";
  const isSingle = items.length === 1;

  useEffect(() => {
    GA4.onModalViewed({ name: title });
  }, [title]);

  return (
    <div className="flex flex-col md:flex-row gap-4 lg:gap-6">
      {items.filter(Boolean).map((offer: GwpTier) => (
        <div key={offer.id} className="flex-1 border border-[#CDD2D1] rounded flex flex-col">
          {/* Banner Header — full width above image/content */}
          <div className={cn(cls, "py-1 px-5 lg:px-2 text-center rounded-t")}>
            <TextElementNova
              text={offer.bannerText}
              className="Body-20-Dia-Strong text-nova-base-white"
            />
          </div>

          {/* Layout Container */}
          <div
            className={cn(
              "flex",
              isSingle ? "flex-col lg:flex-row md:gap-4 lg:gap-0" : "md:flex-col"
            )}
          >
            {/* Image Section */}
            <div
              className={cn(
                isSingle ? "w-full overflow-hidden lg:w-1/2" : "w-[46%] md:w-full overflow-hidden"
              )}
            >
              <Picture
                src={offer.imageSrc}
                alt={offer.imageAlt}
                className="w-full h-auto object-cover aspect-[150/120] md:aspect-auto"
              />
            </div>

            {/* Content Section */}
            <div
              className={cn(
                isSingle
                  ? "w-full px-4 flex flex-col lg:w-1/2"
                  : "w-[54%] md:w-full px-4 flex flex-col",
                offer.id === 1 && !isSingle
                  ? "pt-[5px] sm:pt-[8px] md:pt-4"
                  : "py-[5px] sm:py-[8px] md:py-4 justify-center"
              )}
            >
              {offer.title ? (
                <TextElementNova text={offer.title} className="Body-20-Dia-Strong" />
              ) : null}
              {offer.subtitle ? (
                <TextElementNova
                  text={offer.subtitle}
                  className="lg:pb-2"
                  classes={{
                    default: "Body-10-Dia-Normal",
                    768: "Body-20-Dia-Normal",
                    1024: "Body-20-Dia-Normal",
                  }}
                />
              ) : null}
              <ul className="flex flex-col gap-2 py-2">
                {offer.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <OrangeBullet />
                    <TextElementNova
                      text={item}
                      classes={{
                        default: "Body-10-Dia-Normal",
                        768: "Body-20-Dia-Normal",
                        1024: "Body-10-Dia-Normal",
                      }}
                    />
                  </li>
                ))}
              </ul>
              {offer.valueText ? (
                <TextElementNova text={offer.valueText} className="Body-10-Dia-Strong" />
              ) : null}
              {offer.ref ? (
                <TextElementNova
                  text={offer.ref}
                  className="!text-[14px] Body-10-Dia-Normal italic md:text-center md:pt-4  lg:pt-0 text-nova-transparency-black-70"
                />
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GwpModalContent;
